import { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ChevronDownIcon, ChevronRightIcon, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react'
import { apiClient, TenantSdlMetadata } from '../lib/api'

interface SdlMetadataSectionProps {
  tenantId: string
}

type SectionKey = 'brand_industry' | 'strategic_foci' | 'variables' | 'competitors' | 'entity_model_summary'

interface SectionDef {
  key: SectionKey
  title: string
  description: string
}

const SECTIONS: SectionDef[] = [
  { key: 'brand_industry', title: 'Brand & Industry', description: 'Top-level SDL fields: brand, industry and last_updated.' },
  { key: 'strategic_foci', title: 'Strategic Foci', description: 'The SDL strategic_foci section.' },
  { key: 'variables', title: 'Variables', description: 'The SDL variables section.' },
  { key: 'competitors', title: 'Competitors', description: 'The SDL competitors section.' },
  { key: 'entity_model_summary', title: 'Entity Model Summary', description: 'The SDL entity_model_summary section.' },
]

function sectionValue(metadata: TenantSdlMetadata, key: SectionKey): unknown {
  if (key === 'brand_industry') {
    return { brand: metadata.brand, industry: metadata.industry, last_updated: metadata.last_updated }
  }
  return metadata[key]
}

function isStringOrNull(value: unknown): value is string | null {
  return value === null || value === undefined || typeof value === 'string'
}

interface MetadataCardProps {
  tenantId: string
  section: SectionDef
  value: unknown
  onSaved: (metadata: TenantSdlMetadata) => void
}

function MetadataCard({ tenantId, section, value, onSaved }: MetadataCardProps) {
  const [expanded, setExpanded] = useState(false)
  const [text, setText] = useState('')
  const [seededText, setSeededText] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  // Re-seed the editor whenever the server value changes (initial load, import, save).
  useEffect(() => {
    const pretty = JSON.stringify(value ?? null, null, 2)
    setText(pretty)
    setSeededText(pretty)
    setError(null)
  }, [value])

  const dirty = text !== seededText

  const handleSave = async () => {
    setError(null)
    setSaved(false)
    let parsed: unknown
    try {
      parsed = JSON.parse(text)
    } catch {
      setError('Invalid JSON — fix the syntax before saving.')
      return
    }

    let patch: Partial<TenantSdlMetadata>
    if (section.key === 'brand_industry') {
      if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
        setError('Expected an object with brand, industry and last_updated keys.')
        return
      }
      const obj = parsed as Record<string, unknown>
      if (!isStringOrNull(obj.brand) || !isStringOrNull(obj.industry) || !isStringOrNull(obj.last_updated)) {
        setError('brand, industry and last_updated must each be a string or null.')
        return
      }
      patch = {
        brand: (obj.brand ?? null) as string | null,
        industry: (obj.industry ?? null) as string | null,
        last_updated: (obj.last_updated ?? null) as string | null,
      }
    } else {
      patch = { [section.key]: parsed } as Partial<TenantSdlMetadata>
    }

    setSaving(true)
    try {
      const updated = await apiClient.updateTenantSdlMetadata(tenantId, patch)
      onSaved(updated)
      setSaved(true)
      setTimeout(() => setSaved(false), 2500)
    } catch (err: any) {
      setError(err.message ?? 'Failed to save SDL metadata')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="bg-white border border-gray-100 rounded-xl">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between gap-3 p-4 text-left"
      >
        <div className="flex items-center gap-2">
          {expanded
            ? <ChevronDownIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
            : <ChevronRightIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />}
          <div>
            <h4 className="text-sm font-semibold text-gray-900">{section.title}</h4>
            {expanded && <p className="text-xs text-gray-500 mt-0.5">{section.description}</p>}
          </div>
        </div>
        {dirty && <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 flex-shrink-0">unsaved</span>}
      </button>
      {expanded && (
        <div className="px-4 pb-4">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            spellCheck={false}
            rows={12}
            className="w-full font-mono text-xs border border-gray-200 rounded-lg p-3 text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-y"
          />
          {error && (
            <p className="mt-2 flex items-center gap-1.5 text-xs text-red-600">
              <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" /> {error}
            </p>
          )}
          <div className="mt-2 flex items-center gap-3">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || !dirty}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {saving ? 'Saving…' : 'Save'}
            </button>
            {dirty && !saving && (
              <button
                type="button"
                onClick={() => { setText(seededText); setError(null) }}
                className="text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors"
              >
                Discard changes
              </button>
            )}
            {saved && (
              <span className="inline-flex items-center gap-1.5 text-xs text-emerald-700">
                <CheckCircle2 className="h-3.5 w-3.5" /> Saved
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default function SdlMetadataSection({ tenantId }: SdlMetadataSectionProps) {
  const queryClient = useQueryClient()
  const [expanded, setExpanded] = useState(false)
  const { data: metadata, isLoading, error } = useQuery({
    queryKey: ['tenantSdlMetadata', tenantId],
    queryFn: () => apiClient.getTenantSdlMetadata(tenantId),
    enabled: !!tenantId && expanded,
  })

  const handleSaved = (updated: TenantSdlMetadata) => {
    queryClient.setQueryData(['tenantSdlMetadata', tenantId], updated)
  }

  return (
    <div className="bg-white border border-gray-100 rounded-xl mb-5">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-2 p-5 text-left"
      >
        {expanded
          ? <ChevronDownIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
          : <ChevronRightIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />}
        <h3 className="text-sm font-semibold text-gray-900">SDL Metadata</h3>
      </button>
      {expanded && (
        <div className="px-5 pb-5">
          <p className="text-sm text-gray-500 mb-3">
            The metadata sections of this tenant's SDL. Edits here are reflected in the distributable
            sdl.json the tenant downloads from their admin portal (Settings → AEO).
          </p>
          {isLoading && (
            <div className="flex items-center gap-2 text-sm text-gray-500 border border-gray-100 rounded-xl p-4">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading SDL metadata…
            </div>
          )}
          {!isLoading && !!error && (
            <p className="flex items-center gap-1.5 text-xs text-red-600 border border-gray-100 rounded-xl p-4">
              <AlertTriangle className="h-3.5 w-3.5" /> {(error as Error).message ?? 'Failed to load SDL metadata'}
            </p>
          )}
          {!isLoading && !error && metadata && (
            <div className="space-y-3">
              {SECTIONS.map((section) => (
                <MetadataCard
                  key={section.key}
                  tenantId={tenantId}
                  section={section}
                  value={sectionValue(metadata, section.key)}
                  onSaved={handleSaved}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
